import MediumEditor from 'medium-editor';

function joinPath(parentPath, property) {
    if (!parentPath) {
        return property;
    }
    return property ? `${parentPath}.${property}` : parentPath;
}

function joinName(parentName, name) {
    if (!parentName) {
        return name || '';
    }
    return name ? `${parentName} - ${name}` : parentName;
}

function normalizeType(node) {
    if (node && node.tipo) {
        return node.tipo;
    }
    if (node && node.tipoCampo) {
        return node.tipoCampo;
    }
    if (node && node.children && node.children.length) {
        return 'object';
    }
    return 'string';
}

function normalizeNode(node, parentPath, parentName) {
    const path = joinPath(parentPath, node.property);
    const name = joinName(parentName, node.name);
    const normalized = {
        property: node.property,
        path,
        content: path,
        name,
        tipo: normalizeType(node),
        children: []
    };

    if (node.children && node.children.length) {
        normalized.children = node.children.map(child => normalizeNode(child, path, name));
    }

    return normalized;
}

function normalizeDefinitionTree(definitionTree) {
    const root = {
        path: '',
        content: '',
        property: '',
        name: '',
        tipo: 'root',
        children: []
    };

    if (definitionTree && definitionTree.children && definitionTree.children.length) {
        root.children = definitionTree.children.map(child => normalizeNode(child, '', ''));
    }

    return root;
}

function indexNode(node, map) {
    if (!node) {
        return;
    }
    if (node.path) {
        map[node.path] = node;
    }
    if (node.children && node.children.length) {
        node.children.forEach(child => indexNode(child, map));
    }
}

function flattenToolbarNodes(rootNode) {
    const values = [];
    const walk = (node) => {
        const hasChildren = node.children && node.children.length;
        if (!hasChildren) {
            values.push({
                content: node.path,
                name: node.name,
                tipo: node.tipo,
                children: []
            });
            return;
        }

        if (node.tipo === 'list' || node.tipo === 'object') {
            values.push({
                content: node.path,
                name: node.name,
                tipo: node.tipo,
                children: node.children
            });
        }

        if (node.tipo !== 'list') {
            node.children.forEach(child => walk(child));
        }
    };

    (rootNode.children || []).forEach(child => walk(child));
    return values.sort((left, right) => left.name.localeCompare(right.name));
}

function pickDefaultChildPath(node, parentPath) {
    if (!node || !node.children || !node.children.length) {
        return 'value';
    }

    for (let index = 0; index < node.children.length; index += 1) {
        const child = node.children[index];
        if (child.tipo !== 'list' && child.tipo !== 'object') {
            return child.path.replace(`${parentPath}.`, '');
        }
    }

    const firstChild = node.children[0];
    if (firstChild.tipo === 'object') {
        return pickDefaultChildPath(firstChild, parentPath);
    }

    return firstChild.path.replace(`${parentPath}.`, '');
}

function buildToken(path, type) {
    if (!path) {
        return '[value]';
    }
    if (type === 'html') {
        return `[${path}:html]`;
    }
    return `[${path}]`;
}

var ValorEntidadBlock = MediumEditor.extensions.form.extend({
    name: 'valor',
    action: 'valor',
    aria: 'add structured value',
    title: 'Añadir valor o bloque dinámico',
    valores: [],
    definitionTree: null,
    definitionIndex: {},

    init: function () {
        this.button = this.document.createElement('button');
        this.button.classList.add('medium-editor-action');
        this.button.innerHTML = 'VB';
        this.button.title = this.title;
        this.on(this.button, 'click', this.handleClick.bind(this));
    },

    handleClick: function (event) {
        event.preventDefault();
        event.stopPropagation();
        this.showForm();
        return false;
    },

    getForm: function () {
        if (!this.form) {
            this.form = this.createForm();
        }
        return this.form;
    },

    isDisplayed: function () {
        return this.getForm().style.display === 'block';
    },

    hideForm: function () {
        this.getForm().style.display = 'none';
        this.getSelect().value = '';
    },

    showForm: function () {
        const select = this.getSelect();
        this.base.saveSelection();
        this.hideToolbarDefaultActions();
        this.getForm().style.display = 'block';
        this.setToolbarPosition();
        select.focus();
    },

    destroy: function () {
        if (!this.form) {
            return false;
        }

        if (this.form.parentNode) {
            this.form.parentNode.removeChild(this.form);
        }

        delete this.form;
        return true;
    },

    doFormSave: function () {
        this.base.restoreSelection();
        this.base.checkSelection();
    },

    doFormCancel: function () {
        this.base.restoreSelection();
        this.base.checkSelection();
    },

    createForm: function () {
        const doc = this.document;
        const form = doc.createElement('div');
        const select = doc.createElement('select');
        const close = doc.createElement('a');
        const save = doc.createElement('a');

        form.className = 'medium-editor-toolbar-form';
        form.id = `medium-editor-toolbar-form-valor-block-${this.getEditorId()}`;

        this.on(form, 'click', this.handleFormClick.bind(this));

        select.className = 'medium-editor-toolbar-select';
        form.appendChild(select);
        this.on(select, 'change', this.handleValueChange.bind(this));

        save.setAttribute('href', '#');
        save.className = 'medium-editor-toobar-save';
        save.innerHTML = this.getEditorOption('buttonLabels') === 'fontawesome'
            ? '<i class="fa fa-check"></i>'
            : '&#10003;';
        form.appendChild(save);
        this.on(save, 'click', this.handleSaveClick.bind(this), true);

        close.setAttribute('href', '#');
        close.className = 'medium-editor-toobar-close';
        close.innerHTML = this.getEditorOption('buttonLabels') === 'fontawesome'
            ? '<i class="fa fa-times"></i>'
            : '&times;';
        form.appendChild(close);
        this.on(close, 'click', this.handleCloseClick.bind(this));

        this.refreshSelectOptions();
        return form;
    },

    getSelect: function () {
        return this.getForm().querySelector('select.medium-editor-toolbar-select');
    },

    handleValueChange: function () {
        const select = this.getSelect();
        const selectedValue = select.value;
        if (!selectedValue) {
            return;
        }

        const definition = this.getDefinitionNode(selectedValue);
        if (!definition) {
            return;
        }

        if (definition.tipo === 'list' || definition.tipo === 'object') {
            this.insertStructuredBlock(definition);
        } else {
            this.insertValueToken(definition);
        }

        this.base.checkContentChanged();
    },

    insertValueToken: function (definition) {
        const token = buildToken(definition.path, definition.tipo);
        this.insertNodeWithSpacing(this.document.createTextNode(token));
    },

    insertStructuredBlock: function (definition) {
        const element = this.document.createElement('div');
        element.classList.add('valor', 'valor-block');
        element.classList.add(definition.tipo === 'list' ? 'valor-block-list' : 'valor-block-object');
        element.setAttribute('title', `${this.title} ${definition.name}`);
        element.setAttribute('data-return-type', 'br');
        element.setAttribute('data-type', definition.tipo);
        element.setAttribute('data-block-type', definition.tipo);
        element.setAttribute('data-type-value', definition.path);
        element.setAttribute('data-block-label', definition.name);

        if (definition.tipo === 'list') {
            element.setAttribute('data-recursive-field', '');
            element.setAttribute('data-not-exist', 'DELETE');
            element.setAttribute('data-condition', 'true');
            element.setAttribute('data-block-separator', ', ');
        }

        const defaultChildPath = pickDefaultChildPath(definition, definition.path);
        element.innerHTML = buildToken(defaultChildPath, 'string');
        this.insertNodeWithSpacing(element);
    },

    insertNodeWithSpacing: function (node) {
        let selection;
        if (window.getSelection && (selection = window.getSelection()).rangeCount) {
            const range = selection.getRangeAt(0);
            range.collapse(true);
            range.insertNode(node);
            range.setStartAfter(node);
            const spacer = this.document.createTextNode('\u00A0');
            range.insertNode(spacer);
            range.setStartAfter(spacer);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    },

    handleFormClick: function (event) {
        event.stopPropagation();
    },

    handleSaveClick: function (event) {
        event.preventDefault();
        this.doFormSave();
    },

    handleCloseClick: function (event) {
        event.preventDefault();
        this.doFormCancel();
    },

    refreshSelectOptions: function () {
        if (!this.form) {
            return;
        }

        const select = this.getSelect();
        if (!select) {
            return;
        }

        Array.from(select.childNodes).forEach(node => node.remove());

        const emptyOption = this.document.createElement('option');
        emptyOption.innerHTML = '';
        emptyOption.value = '';
        select.appendChild(emptyOption);

        this.valores.forEach(optionRes => {
            const option = this.document.createElement('option');
            option.innerHTML = optionRes.name;
            option.value = optionRes.content;
            select.appendChild(option);
        });
    },

    loadValoresEntidad: function (values) {
        this.valores = (values || []).slice().sort((left, right) => left.name.localeCompare(right.name));
        this.definitionIndex = {};
        this.valores.forEach(value => {
            this.definitionIndex[value.content] = value;
        });
        this.refreshSelectOptions();
    },

    loadDefinitionTree: function (definitionTree) {
        this.definitionTree = normalizeDefinitionTree(definitionTree);
        const definitionIndex = {};
        indexNode(this.definitionTree, definitionIndex);
        this.definitionIndex = definitionIndex;
        this.valores = flattenToolbarNodes(this.definitionTree);
        this.refreshSelectOptions();
    },

    getDefinitionNode: function (path) {
        return this.definitionIndex ? this.definitionIndex[path] : null;
    },

    getRootProperties: function () {
        return (this.definitionTree && this.definitionTree.children ? this.definitionTree.children : []).map(child => child.property);
    }
});

export default ValorEntidadBlock;